import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  createUserWithEmailAndPassword,
  signOut,
  type User,
} from 'firebase/auth';
import { auth, isFirebaseConfigured } from './firebase';
import { api, setAuthTokenProvider } from './api';

type Balance = {
  credits: number;
  freeRestorationsRemaining: number;
  canRestore: boolean;
  creditMode?: string;
  stage?: string;
  restorationsCurrentlyFree?: boolean;
  creditsEnforced?: boolean;
};

type ProductConfig = {
  stage: string;
  creditMode: string;
  creditsEnforced: boolean;
  restorationsCurrentlyFree: boolean;
  watermarkExportsEnabled?: boolean;
  billingEnabled?: boolean;
  planLabel?: string;
  upscaleEnabled?: boolean;
  upscaleCreditCost?: number;
};

type AuthContextValue = {
  user: User | null;
  loading: boolean;
  token: string | null;
  balance: Balance | null;
  productConfig: ProductConfig | null;
  isDevAuth: boolean;
  refreshBalance: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
  signInGoogle: () => Promise<void>;
  signInEmail: (email: string, password: string) => Promise<void>;
  signUpEmail: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const DEV_USER = {
  uid: 'dev-user',
  email: 'dev@kinvera.local',
  displayName: 'Kinvera Dev',
} as unknown as User;

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isDevAuth = import.meta.env.VITE_USE_DEV_AUTH === 'true';
  const [user, setUser] = useState<User | null>(isDevAuth ? DEV_USER : null);
  const [loading, setLoading] = useState(!isDevAuth);
  const [token, setToken] = useState<string | null>(null);
  const [balance, setBalance] = useState<Balance | null>(null);
  const [productConfig, setProductConfig] = useState<ProductConfig | null>(null);

  const getIdToken = useCallback(async (): Promise<string | null> => {
    if (isDevAuth) return null;
    if (!auth?.currentUser) return null;
    try {
      return await auth.currentUser.getIdToken(/* forceRefresh */ false);
    } catch (err) {
      console.warn('getIdToken failed:', err);
      return null;
    }
  }, [isDevAuth]);

  useEffect(() => {
    setAuthTokenProvider(getIdToken);
    return () => setAuthTokenProvider(null);
  }, [getIdToken]);

  useEffect(() => {
    api
      .config()
      .then((cfg) =>
        setProductConfig({
          stage: cfg.stage,
          creditMode: cfg.creditMode,
          creditsEnforced: cfg.creditsEnforced,
          restorationsCurrentlyFree: cfg.restorationsCurrentlyFree,
          watermarkExportsEnabled: cfg.watermarkExportsEnabled !== false,
          billingEnabled: Boolean(cfg.billingEnabled),
          planLabel: cfg.planLabel || 'Pay as you go',
          upscaleEnabled: cfg.upscaleEnabled !== false,
          upscaleCreditCost: Number(cfg.upscaleCreditCost ?? 1),
        })
      )
      .catch((err) => console.warn('Config fetch failed:', err));
  }, []);

  const refreshBalance = useCallback(async () => {
    try {
      const idToken = await getIdToken();
      const me = await api.me(idToken);
      setBalance({
        credits: me.credits,
        freeRestorationsRemaining: me.freeRestorationsRemaining,
        canRestore: me.canRestore,
        creditMode: me.creditMode,
        stage: me.stage,
        restorationsCurrentlyFree: me.restorationsCurrentlyFree,
        creditsEnforced: me.creditsEnforced,
      });
      if (me.stage || me.creditMode) {
        setProductConfig((prev) => ({
          stage: me.stage || 'alpha',
          creditMode: me.creditMode || 'disabled',
          creditsEnforced: Boolean(me.creditsEnforced),
          restorationsCurrentlyFree: Boolean(me.restorationsCurrentlyFree),
          watermarkExportsEnabled: me.watermarkExportsEnabled !== false,
          billingEnabled: Boolean(me.billingEnabled),
          planLabel: me.planLabel || 'Pay as you go',
          upscaleEnabled: prev?.upscaleEnabled !== false,
          upscaleCreditCost: prev?.upscaleCreditCost ?? 1,
        }));
      }
    } catch (err) {
      console.warn('Balance refresh failed:', err);
    }
  }, [getIdToken]);

  useEffect(() => {
    if (isDevAuth) {
      setLoading(false);
      refreshBalance();
      return;
    }
    if (!isFirebaseConfigured || !auth) {
      setLoading(false);
      return;
    }
    return onAuthStateChanged(auth, async (next) => {
      setUser(next);
      if (next) {
        try {
          const idToken = await next.getIdToken();
          setToken(idToken);
        } catch (err) {
          console.warn('Failed to read ID token after auth change:', err);
          setToken(null);
        }
      } else {
        setToken(null);
        setBalance(null);
      }
      setLoading(false);
    });
  }, [isDevAuth, refreshBalance]);

  useEffect(() => {
    if (user && (token || isDevAuth)) refreshBalance();
  }, [user, token, isDevAuth, refreshBalance]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      token,
      balance,
      productConfig,
      isDevAuth,
      refreshBalance,
      getIdToken,
      signInGoogle: async () => {
        if (!auth) throw new Error('Firebase Auth is not configured');
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({ prompt: 'select_account' });
        await signInWithPopup(auth, provider);
      },
      signInEmail: async (email, password) => {
        if (!auth) throw new Error('Firebase Auth is not configured');
        await signInWithEmailAndPassword(auth, email, password);
      },
      signUpEmail: async (email, password) => {
        if (!auth) throw new Error('Firebase Auth is not configured');
        await createUserWithEmailAndPassword(auth, email, password);
      },
      logout: async () => {
        if (isDevAuth) return;
        if (auth) await signOut(auth);
      },
    }),
    [user, loading, token, balance, productConfig, isDevAuth, refreshBalance, getIdToken]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
